/**
 * SpawnMarker — the explore-mode spawn gizmo.
 *
 * A flat ring on the terrain surface showing where the player will spawn. In explore
 * mode the spawn stays pinned to screen center (or the user taps/drags to move it) and a
 * React Play button — connected to the ring by a fixed-height UI line, not a 3D one —
 * starts 1st-person play there.
 *
 * Module singleton (one marker, one scene), mirroring the `controls` singleton so both
 * the game loop and React UI can drive it without prop threading.
 */

import * as THREE from 'three';
import { PLAYER_HEIGHT, SPAWN_HEIGHT_OFFSET } from '@worldify/shared';
import { columnProbe } from './columnProbe';
import type { TerrainProbe } from './TerrainProbe';

const RING_INNER = 0.7;
const RING_OUTER = 1.05;
const MARKER_COLOR = 0xffffff;

let scene: THREE.Scene | null = null;
let terrain: TerrainProbe | null = null;
let group: THREE.Group | null = null;

let placed = false;
let armed = false;                        // Play requested → next Playing entry uses the marker
const spawnPos = new THREE.Vector3();     // player spawn (surface + player height)
const basePoint = new THREE.Vector3();    // ring position (on surface); Play-button anchor

const raycaster = new THREE.Raycaster();
const _ndc = new THREE.Vector2();
const _down = new THREE.Vector3(0, -1, 0);

/** Build the marker group + add it to the scene (hidden until placed). */
export function initSpawnMarker(s: THREE.Scene, t: TerrainProbe): void {
  scene = s;
  terrain = t;
  if (group) return;

  group = new THREE.Group();
  group.renderOrder = 999;
  group.visible = false;

  const mat = new THREE.MeshBasicMaterial({
    color: MARKER_COLOR, transparent: true, opacity: 0.85, depthTest: false, side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(RING_INNER, RING_OUTER, 40), mat);
  ring.rotation.x = -Math.PI / 2;   // lie flat on the ground
  ring.renderOrder = 999;
  ring.frustumCulled = false;
  group.add(ring);

  scene.add(group);
}

/**
 * Cast a screen NDC point against the terrain; returns the surface hit or null.
 *
 * DELIBERATELY a mesh raycast, where the rest of the engine moved to the voxel field. This is
 * explore-mode UI picking: it must hit whatever is actually DRAWN, and at explore zoom most of
 * that is coarse far-LOD ring geometry kilometres out. The physics field is lod-0 only and holds
 * nothing at those distances, so a field probe would simply miss the terrain the player is looking
 * at. Picking against pixels is the correct answer for a tap; collision is the thing that must
 * agree with the field, and it does.
 */
export function raycastMarkerNDC(ndc: { x: number; y: number }, camera: THREE.Camera): THREE.Vector3 | null {
  if (!terrain) return null;
  _ndc.set(ndc.x, ndc.y);
  raycaster.setFromCamera(_ndc, camera);
  // Far-LOD rings put terrain kilometres out; a zoomed-out explore tap must still land on it.
  raycaster.far = 8000;
  const hits = raycaster.intersectObjects(terrain.getSolidMeshes(), false);
  return hits.length > 0 ? hits[0].point.clone() : null;
}

/** Downward probe at an (x,z) column; returns the surface point or null. */
function raycastColumn(x: number, z: number): THREE.Vector3 | null {
  if (!terrain) return null;
  const meshes = terrain.getSolidMeshes();
  // Ray bounds come from the loaded terrain: at kilometre scale a fixed 200 m origin starts below the
  // summits and the cast misses them. See columnProbe.
  const probe = columnProbe(meshes);
  raycaster.set(new THREE.Vector3(x, probe.originY, z), _down);
  raycaster.far = probe.far;
  const hits = raycaster.intersectObjects(meshes, false);
  return hits.length > 0 ? hits[0].point.clone() : null;
}

/** Place the marker at a world surface point. */
export function placeMarkerAt(hit: THREE.Vector3): void {
  if (!group) return;
  basePoint.copy(hit);
  spawnPos.set(hit.x, hit.y + PLAYER_HEIGHT + SPAWN_HEIGHT_OFFSET, hit.z);
  group.position.copy(hit);
  group.visible = true;
  placed = true;
}

/** Place from a screen tap (NDC). Returns true if it hit terrain. */
export function placeMarkerFromNDC(ndc: { x: number; y: number }, camera: THREE.Camera): boolean {
  const hit = raycastMarkerNDC(ndc, camera);
  if (!hit) return false;
  placeMarkerAt(hit);
  return true;
}

/** Auto-place at an (x,z) column (e.g. the last player position). Returns true if placed. */
export function placeMarkerAtColumn(x: number, z: number): boolean {
  const hit = raycastColumn(x, z);
  if (!hit) return false;
  placeMarkerAt(hit);
  return true;
}

export function isMarkerPlaced(): boolean { return placed; }
export function getSpawnPosition(): THREE.Vector3 { return spawnPos; }
export function getMarkerBase(): THREE.Vector3 { return basePoint; }

export function setMarkerVisible(v: boolean): void {
  if (group) group.visible = v && placed;
}

/** Arm the marker so the next Playing entry spawns at it (called by the Play button). */
export function armMarkerSpawn(): void { armed = true; }

/** If armed + placed, return the spawn position and clear the arm; else null. */
export function consumeMarkerSpawn(): THREE.Vector3 | null {
  if (!armed || !placed) { armed = false; return null; }
  armed = false;
  return spawnPos.clone();
}

/** Reset placement (e.g. on world switch) so it re-auto-places for the new world. */
export function resetMarker(): void {
  placed = false;
  armed = false;
  if (group) group.visible = false;
}
